import React, { useEffect, useState } from 'react';

import type { IntlAddress } from '@/typings/api-models';
import stateCodeToName from '@/constants/stateCodeToName';
import { Input } from '../Input';
import Select from '../Select';
import s from '../AddressUS/AddressUS.module.scss';
import Fieldset from '../shared/Fieldset';
import Button from '../../Button';
import { COUNTRIES, DEFAULT_VALUE } from './constants';

const AddressIntl: React.FC<
  Pick<Parameters<typeof Fieldset>[0], 'label' | 'description' | 'required'> & {
    value: IntlAddress | null;
    onChange: (address: IntlAddress) => void;
    disabled?: boolean;
    onBlur?: () => void;
    onFocus?: () => void;
  } & {
    error?: string;
  }
> = ({ label, description, required, value, onChange, onBlur, onFocus, disabled, error }) => {
  const [address, setAddress] = useState(value || DEFAULT_VALUE);

  const handleInputChange = (field: string) => (v: string) => {
    const newAddress = { ...address, [field]: v };

    setAddress(newAddress);
    onChange(newAddress);
  };

  useEffect(() => {
    setAddress(value ?? DEFAULT_VALUE);
  }, [value]);

  return (
    <Fieldset label={label} description={description} required={required} error={error}>
      <div className={s['address-us']}>
        {address !== DEFAULT_VALUE && (
          <Button variant="secondary" className={s.clear} size="xs" onClick={() => setAddress(DEFAULT_VALUE)}>
            Clear
          </Button>
        )}
        <Input
          label="Street address"
          compact
          value={address.line1}
          onChange={handleInputChange('line1')}
          disabled={disabled}
          onBlur={onBlur}
          onFocus={onFocus}
        />
        <Input
          label="Apartment / Floor / Suite number"
          compact
          value={address.line2}
          onChange={handleInputChange('line2')}
          disabled={disabled}
          onBlur={onBlur}
          onFocus={onFocus}
          optional
        />
        {address.country === 'US' && (
          <Select
            label="Country"
            compact
            placeholder="Select"
            options={COUNTRIES}
            value={address.country}
            onChange={handleInputChange('country')}
            disabled={disabled}
            onFocus={onFocus}
          />
        )}
        <div className={s.row}>
          {address.country !== 'US' && (
            <Select
              label="Country"
              compact
              placeholder="Select"
              options={COUNTRIES}
              value={address.country}
              onChange={handleInputChange('country')}
              disabled={disabled}
              onFocus={onFocus}
            />
          )}
          <Input
            label="City"
            compact
            value={address.city}
            onChange={handleInputChange('city')}
            disabled={disabled}
            onBlur={onBlur}
            onFocus={onFocus}
          />
          {address.country === 'US' && (
            <Select
              label="State"
              compact
              placeholder="Select"
              options={stateCodeToName}
              value={address.state}
              onChange={handleInputChange('state')}
              disabled={disabled}
              onFocus={onFocus}
            />
          )}
          <Input
            label="Post Code"
            compact
            value={address.postCode}
            onChange={handleInputChange('postCode')}
            onBlur={onBlur}
            disabled={disabled}
            onFocus={onFocus}
          />
        </div>
      </div>
    </Fieldset>
  );
};

export default AddressIntl;
